import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { errorMessage } from '../api/client';
import type { NamedRef } from '../api/catalog';

/** How many matches to draw. */
const MAX_MATCHES = 30;
const SEARCH_DEBOUNCE_MS = 200;

/**
 * Pick authors, categories or a publisher by typing part of the name.
 *
 * Nothing is listed until something is typed: there are thousands of authors,
 * and a dropdown of all of them is a scroll nobody finishes. What is chosen
 * sits above the box as chips, so it stays readable without opening anything.
 *
 * A short list is filtered here from `options`. A long one is given `search`
 * instead and asks the server for what was typed — `options` is then only the
 * names already known, which is what the chips are labelled from.
 *
 * A name that is not on the list can be created from here, but only by
 * clicking the row that offers it — never on Enter. The barcode scanner types
 * into whatever holds focus and ends with Enter, and a stray scan landing in
 * this box must not be able to create an author called 9788126415458.
 */
export default function RefPicker({
  id,
  label,
  options,
  value,
  onChange,
  onPick,
  single = false,
  search,
  onCreate,
  loading = false,
  error,
}: {
  id: string;
  label: string;
  options: NamedRef[];
  value: string[];
  onChange: (next: string[]) => void;
  /** Told about every record chosen, so the parent can keep its name. */
  onPick?: (ref: NamedRef) => void;
  /** One choice at most; picking another replaces it. */
  single?: boolean;
  /** Ask the server for names matching what was typed. Must be a stable function. */
  search?: (q: string, signal: AbortSignal) => Promise<NamedRef[]>;
  /** Create the typed name and hand back its record. */
  onCreate?: (name: string) => Promise<NamedRef>;
  loading?: boolean;
  error?: string;
}) {
  const [text, setText] = useState('');
  const [creating, setCreating] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const [remote, setRemote] = useState<NamedRef[]>([]);
  // Which term `remote` answers. Results lag the typing by the debounce, and
  // a list for the previous term must not be offered against this one.
  const [remoteFor, setRemoteFor] = useState('');

  const byId = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);
  const typed = text.trim();
  const q = typed.toLowerCase();
  // One letter matches half the catalogue; wait for a second before asking.
  const askable = search ? q.length >= 2 : q.length >= 1;
  const answered = !search || remoteFor === q;

  useEffect(() => {
    if (!search || q.length < 2) return;
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      search(q, controller.signal)
        .then((found) => {
          setRemote(found);
          setRemoteFor(q);
          setProblem(null);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          setRemote([]);
          setRemoteFor(q);
          setProblem(errorMessage(err, 'Search failed.'));
        });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [search, q]);

  const matches = useMemo(() => {
    if (!askable || !answered) return [];
    const pool = search
      ? remote
      : options.filter((o) => o.name.toLowerCase().includes(q));
    const hits = pool.filter((o) => !value.includes(o.id));
    // Names that start with what was typed come first: "ben" should offer
    // Benyamin ahead of every Reuben.
    hits.sort(
      (a, b) =>
        Number(b.name.toLowerCase().startsWith(q)) -
        Number(a.name.toLowerCase().startsWith(q)),
    );
    return hits.slice(0, MAX_MATCHES);
  }, [askable, answered, search, remote, options, value, q]);

  const exactExists = (search ? remote : options).some(
    (o) => o.name.trim().toLowerCase() === q,
  );

  function pick(ref: NamedRef) {
    onPick?.(ref);
    onChange(single ? [ref.id] : value.includes(ref.id) ? value : [...value, ref.id]);
    setText('');
    setProblem(null);
  }

  async function create() {
    if (!onCreate || !typed || creating) return;
    setCreating(true);
    setProblem(null);
    try {
      pick(await onCreate(typed));
    } catch (err) {
      setProblem(errorMessage(err, `Could not add “${typed}”.`));
    } finally {
      setCreating(false);
    }
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') {
      setText('');
      return;
    }
    if (e.key !== 'Enter') return;
    e.preventDefault();
    // Only when there is exactly one thing it could mean.
    if (matches.length === 1) pick(matches[0]);
  }

  return (
    <div className="picker">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>

      {value.length > 0 && (
        <div className="picker-chosen">
          {value.map((refId) => (
            <span key={refId} className="picker-chip">
              {byId.get(refId)?.name ?? '…'}
              <button
                type="button"
                onClick={() => onChange(value.filter((v) => v !== refId))}
                aria-label={`Remove ${byId.get(refId)?.name ?? label}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      <input
        id={id}
        className="input"
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={loading ? 'Loading…' : 'Type a name to search'}
        autoComplete="off"
        spellCheck={false}
      />

      {askable && (
        <div className="picker-list">
          {!answered && <p className="muted small picker-none">Searching…</p>}
          {matches.map((ref) => (
            <button
              key={ref.id}
              type="button"
              className="picker-option"
              // Keeps the focus in the box, so the next name can be typed
              // straight after choosing this one.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(ref)}
            >
              {ref.name}
            </button>
          ))}
          {/* Offered only once the list has answered, so what already exists
              is seen before a second spelling of it is made. */}
          {answered && onCreate && !exactExists && !loading && (
            <button
              type="button"
              className="picker-option picker-create"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => void create()}
              disabled={creating}
            >
              {creating ? 'Adding…' : `+ Add “${typed}” as new`}
            </button>
          )}
          {answered && !matches.length && (!onCreate || exactExists) && (
            <p className="muted small picker-none">
              {exactExists ? 'Already chosen.' : 'Nothing matches.'}
            </p>
          )}
        </div>
      )}

      {(problem ?? error) && (
        <p className="error small" role="alert">
          {problem ?? error}
        </p>
      )}
    </div>
  );
}
